import { ref, watch, onScopeDispose, type Ref } from 'vue';
import axios from 'axios';
import type { IAppointment } from '@nova/core';

import cometApiService from '@/services/cometApiService';

export const DEBOUNCE_MS = 1000;

export interface UseAppointmentLookupOptions {
  warehouseId: Ref<string>;
  onApiError: (error: unknown) => void;
}

export function useAppointmentLookup({ warehouseId, onApiError }: UseAppointmentLookupOptions) {
  const identifier = ref('');
  const isLookingUp = ref(false);
  const appointment = ref<IAppointment | null>(null);
  const notFoundError = ref(false);

  const { getAppointment } = cometApiService();

  let debounceTimer: ReturnType<typeof setTimeout> | null = null;

  // Request generation to reject stale responses
  let inFlightValue: string | null = null;
  let requestGeneration = 0;

  async function search(): Promise<boolean> {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }

    const value = identifier.value.trim();
    const generation = ++requestGeneration;

    if (!value) {
      appointment.value = null;
      notFoundError.value = false;
      return false;
    }

    isLookingUp.value = true;
    notFoundError.value = false;
    inFlightValue = value;

    try {
      const result = await getAppointment(warehouseId.value, value);

      if (generation !== requestGeneration || identifier.value.trim() !== value) {
        return false;
      }

      appointment.value = result;

      return true;
    } catch (error) {
      if (generation !== requestGeneration || identifier.value.trim() !== value) {
        return false;
      }

      appointment.value = null;

      if (axios.isAxiosError(error) && error.response?.status === 404) {
        notFoundError.value = true;
      } else {
        onApiError(error);
      }

      return false;
    } finally {
      if (generation === requestGeneration && identifier.value.trim() === value) {
        isLookingUp.value = false;
      }
      if (generation === requestGeneration && inFlightValue === value) {
        inFlightValue = null;
      }
    }
  }

  watch(identifier, () => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
      debounceTimer = null;
    }
    appointment.value = null;
    notFoundError.value = false;

    const value = identifier.value.trim();

    if (value === inFlightValue) {
      isLookingUp.value = true;
      return;
    }

    requestGeneration += 1;
    inFlightValue = null;

    if (!value) {
      isLookingUp.value = false;
      return;
    }

    isLookingUp.value = true;
    debounceTimer = setTimeout(search, DEBOUNCE_MS);
  });

  onScopeDispose(() => {
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }
  });

  return {
    identifier,
    isLookingUp,
    appointment,
    notFoundError,
    search
  };
}
